import compareCaseInsensitive from 'lib/util/compareCaseInsensitive'
import ProductCategory from 'pages/Home/ProductCategory'
import RecordedWebinarType from 'pages/Home/RecordedWebinarType'
import Series from 'pages/Home/Series'
import SkillLevel from 'pages/Home/SkillLevel'
import StartTime from 'pages/Home/StartTime'
import { isInNonAdminDateRange, isInPast } from 'pages/Home/UpcomingWebinarsSection/util'
import UpcomingWebinarType from 'pages/Home/UpcomingWebinarType'
import VideoType from 'pages/Home/VideoType'

import RecordedWebinarSort from '../RecordedWebinarSort'
import { isQuickTips } from '../SeriesFilter/KnownSeries'

type Webinar = RecordedWebinarType | UpcomingWebinarType
type Item = RecordedWebinarType | UpcomingWebinarType | VideoType
type CategoryKey = 'rise' | 'storyline' | 'more'
type VideoCategoryKey = 'rise' | 'storyline'

function isRecordedWebinar(webinar: Webinar): webinar is RecordedWebinarType {
  return (webinar as RecordedWebinarType).seriesId !== undefined
}

function isVideo(item: Item): item is VideoType {
  return (item as VideoType).upload !== undefined
}

function getProductCategory(item: Item, categoryKey: CategoryKey) {
  if (isVideo(item)) {
    return item[categoryKey as VideoCategoryKey]
  }
  if (isRecordedWebinar(item)) {
    return item[categoryKey]
  }

  return item.recordedWebinar[categoryKey]
}

function isInSkillLevel(webinar: Webinar, selectedSkillLevel: SkillLevel): boolean {
  if (selectedSkillLevel === SkillLevel.All) {
    return true
  }
  if (isRecordedWebinar(webinar)) {
    return (webinar as RecordedWebinarType).skillLevel === selectedSkillLevel
  }

  return (webinar as UpcomingWebinarType).recordedWebinar.skillLevel === selectedSkillLevel
}

export function isInProductCategory(item: Item, productCategory: ProductCategory) {
  return (
    (productCategory === ProductCategory.Rise && getProductCategory(item, 'rise')) ||
    (productCategory === ProductCategory.Storyline && getProductCategory(item, 'storyline')) ||
    (productCategory === ProductCategory.More && getProductCategory(item, 'more'))
  )
}

function isInSelectedSeries(webinar: Webinar, selectedSeries: Series) {
  if (isRecordedWebinar(webinar)) {
    return (webinar as RecordedWebinarType).seriesId === selectedSeries.id
  }

  return (webinar as UpcomingWebinarType).recordedWebinar.seriesId === selectedSeries.id
}

export function isPublished(webinar: Webinar): boolean {
  if (isRecordedWebinar(webinar)) {
    return (webinar as RecordedWebinarType).published && (webinar as RecordedWebinarType).series.published
  }

  return (webinar as UpcomingWebinarType).published && (webinar as UpcomingWebinarType).recordedWebinar.series.published
}

function getAllRecordedWebinars(
  webinars: RecordedWebinarType[],
  productCategory: ProductCategory,
  selectedSkillLevel: SkillLevel,
  showAdminControls: boolean,
  selectedSort: RecordedWebinarSort
) {
  const filteredWebinars = webinars.filter(
    webinar =>
      isInProductCategory(webinar, productCategory) &&
      isInSkillLevel(webinar, selectedSkillLevel) &&
      (showAdminControls || isPublished(webinar))
  )
  if (selectedSort === RecordedWebinarSort.Popular) {
    return filteredWebinars.sort((a, b) => b.viewCount - a.viewCount || compareCaseInsensitive(a.title, b.title))
  }
  if (selectedSort === RecordedWebinarSort.Title) {
    return filteredWebinars.sort((a, b) => compareCaseInsensitive(a.title, b.title))
  }

  return filteredWebinars.sort(
    (a, b) =>
      new Date(b.latestUpcomingDate || 0).getTime() - new Date(a.latestUpcomingDate || 0).getTime() ||
      compareCaseInsensitive(a.title, b.title)
  )
}

export function filterRecordedWebinarsForSearch(
  webinars: RecordedWebinarType[],
  productCategory: ProductCategory,
  showAdminControls: boolean
) {
  return webinars.filter(
    webinar => (showAdminControls || isPublished(webinar)) && isInProductCategory(webinar, productCategory)
  )
}

function getRecordedWebinarsForSelectedSeries(
  webinars: RecordedWebinarType[],
  productCategory: ProductCategory,
  selectedSeries: Series,
  showAdminControls: boolean
) {
  const filteredWebinars = webinars.filter(
    webinar =>
      isInProductCategory(webinar, productCategory) &&
      isInSelectedSeries(webinar, selectedSeries) &&
      (showAdminControls || isPublished(webinar))
  )
  const sorted = filteredWebinars.sort((a, b) => compareCaseInsensitive(a.title, b.title))
  if (isQuickTips(selectedSeries)) {
    // Quick Tips are sorted in reverse since "Quick Tips N+1" is always more recent than "Quick Tips N"
    return sorted.reverse()
  }

  return sorted
}

export function filterRecordedWebinars(
  webinars: RecordedWebinarType[],
  productCategory: ProductCategory,
  selectedSeries: Series,
  selectedSkillLevel: SkillLevel,
  showAdminControls: boolean,
  selectedSort: RecordedWebinarSort
) {
  return !selectedSeries
    ? getAllRecordedWebinars(webinars, productCategory, selectedSkillLevel, showAdminControls, selectedSort)
    : getRecordedWebinarsForSelectedSeries(webinars, productCategory, selectedSeries, showAdminControls)
}

export function filterSeries(seriesList: Series[], showAdminControls: boolean) {
  return seriesList.filter(series => showAdminControls || series.published)
}

function isInStartTime(upcomingWebinar: UpcomingWebinarType, selectedStartTime: StartTime) {
  if (selectedStartTime === StartTime.All) {
    return true
  }
  const localDate = new Date(upcomingWebinar.date)
  const hours = localDate.getHours()
  if (selectedStartTime === StartTime.Morning) {
    return hours >= 0 && hours < 12
  }
  if (selectedStartTime === StartTime.Afternoon) {
    return hours >= 12 && hours < 18
  }
  if (selectedStartTime === StartTime.Evening) {
    return hours >= 18 && hours < 24
  }

  return false
}

export function isInDateRange(
  upcomingWebinar: UpcomingWebinarType,
  showAdminControls: boolean,
  hidePastWebinars: boolean
) {
  if (showAdminControls) {
    if (hidePastWebinars) {
      return !isInPast(upcomingWebinar)
    }

    return true
  }

  return isInNonAdminDateRange(upcomingWebinar)
}

function dedupePastUpcomingWebinars(upcomingWebinars: UpcomingWebinarType[]) {
  interface LatestPastUpcomingIdByRecordedId {
    [key: string]: string
  }
  const initialVal: LatestPastUpcomingIdByRecordedId = {}
  const latestPastUpcomingIdByRecordedId: LatestPastUpcomingIdByRecordedId = upcomingWebinars
    .filter(isInPast)
    .reduce((latestByRecordedId, upcomingWebinar) => {
      latestByRecordedId[upcomingWebinar.recordedWebinar.id] = upcomingWebinar.id

      return latestByRecordedId
    }, initialVal)

  return upcomingWebinars.filter(upcomingWebinar => {
    if (!isInPast(upcomingWebinar)) {
      return true
    }

    return latestPastUpcomingIdByRecordedId[upcomingWebinar.recordedWebinar.id] === upcomingWebinar.id
  })
}

export function filterUpcomingWebinars(
  upcomingWebinars: UpcomingWebinarType[],
  productCategory: ProductCategory,
  selectedSeries: Series,
  selectedSkillLevel: SkillLevel,
  selectedStartTime: StartTime,
  showAdminControls: boolean,
  hidePastWebinars: boolean
) {
  const filteredUpcomingWebinars = upcomingWebinars.filter(
    upcomingWebinar =>
      isInDateRange(upcomingWebinar, showAdminControls, hidePastWebinars) &&
      isInProductCategory(upcomingWebinar, productCategory) &&
      isInSkillLevel(upcomingWebinar, selectedSkillLevel) &&
      isInStartTime(upcomingWebinar, selectedStartTime) &&
      (!selectedSeries || isInSelectedSeries(upcomingWebinar, selectedSeries)) &&
      (showAdminControls || isPublished(upcomingWebinar))
  )

  if (showAdminControls && !hidePastWebinars) {
    return dedupePastUpcomingWebinars(filteredUpcomingWebinars)
  }

  return filteredUpcomingWebinars
}

export function filterUpcomingWebinarsForSearch(
  upcomingWebinars: UpcomingWebinarType[],
  productCategory: ProductCategory,
  showAdminControls: boolean,
  hidePastWebinars: boolean
) {
  return upcomingWebinars.filter(
    upcomingWebinar =>
      (showAdminControls || isPublished(upcomingWebinar)) &&
      isInProductCategory(upcomingWebinar, productCategory) &&
      isInDateRange(upcomingWebinar, showAdminControls, hidePastWebinars)
  )
}

export function filterVideos(videos: VideoType[], productCategory: ProductCategory, showAdminControls: boolean) {
  return videos.filter(video =>
    showAdminControls
      ? !video.published || isInProductCategory(video, productCategory)
      : video.published && isInProductCategory(video, productCategory)
  )
}
